/**
 * Copyright Zendesk, Inc.
 *
 * Use of this source code is governed under the Apache License, Version 2.0
 * found at http://www.apache.org/licenses/LICENSE-2.0.
 */

import * as React from 'react';
import React__default, { useContext, useMemo, useEffect, Children, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css, keyframes, ThemeContext } from 'styled-components';
import { math, rgba, readableColor } from 'polished';
import { retrieveComponentStyles, DEFAULT_THEME, getLineHeight, getColor, PALETTE, useDocument } from '@zendeskgarden/react-theming';
import { useAccordion } from '@zendeskgarden/container-accordion';
import { getControlledValue } from '@zendeskgarden/container-utilities';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends$2() {
  _extends$2 = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$2.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var ChromeContext = React__default.createContext({
  hue: 'chromeHue'
});
var useChromeContext = function useChromeContext() {
  return useContext(ChromeContext);
};

var COMPONENT_ID$r = 'chrome.chrome';
var StyledChrome = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$r,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledChrome",
  componentId: "sc-1uqm6u6-0"
})(["display:flex;position:relative;margin:0;height:100vh;overflow-y:hidden;font-family:", ";direction:", ";", ";"], function (props) {
  return props.theme.fonts.system;
}, function (props) {
  return props.theme.rtl && 'rtl';
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$r, props);
});
StyledChrome.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$q = 'chrome.header_item_icon';
var StyledHeaderItemIcon = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$q,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledHeaderItemIcon",
  componentId: "sc-1jhhp6z-0"
})(["transition:transform 0.25s ease-in-out;margin:0 3px;width:", ";min-width:", ";height:", ";", ";"], function (props) {
  return props.theme.iconSizes.md;
}, function (props) {
  return props.theme.iconSizes.md;
}, function (props) {
  return props.theme.iconSizes.md;
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$q, props);
});
StyledHeaderItemIcon.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$p = 'chrome.base_header_item';
var getHeaderItemSize = function getHeaderItemSize(props) {
  return "".concat(props.theme.space.base * 7.5, "px");
};
var sizeStyles$4 = function sizeStyles(props) {
  var size = props.theme.space.base * 7.5;
  return css(["padding:0 3px;min-width:", "px;height:", ";line-height:", ";"], size, props.maxY ? '100%' : "".concat(size, "px"), getLineHeight(size, props.theme.fontSizes.md));
};
var StyledBaseHeaderItem = styled.button.attrs({
  'data-garden-id': COMPONENT_ID$p,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledBaseHeaderItem",
  componentId: "sc-1qua7h6-0"
})(["display:inline-flex;position:relative;flex:", ";align-items:center;justify-content:", ";order:1;transition:box-shadow 0.1s ease-in-out,color 0.1s ease-in-out;z-index:0;margin:", ";border:none;border-radius:", ";background:transparent;text-decoration:none;white-space:nowrap;color:inherit;font-size:inherit;", " ", ";"], function (props) {
  return props.maxX && '1';
}, function (props) {
  return props.maxX ? 'start' : 'center';
}, function (props) {
  return "0 ".concat(props.theme.space.base * 3, "px");
}, function (props) {
  if (props.isRound) {
    return '100%';
  }
  if (props.maxY) {
    return '0';
  }
  return props.theme.borderRadii.md;
}, sizeStyles$4, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$p, props);
});
StyledBaseHeaderItem.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$o = 'chrome.header_item_text';
var clippedStyling = css(["position:absolute;margin:0;clip:rect(1px,1px,1px,1px);width:1px;height:1px;overflow:hidden;white-space:nowrap;"]);
var StyledHeaderItemText = styled.span.attrs({
  'data-garden-id': COMPONENT_ID$o,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledHeaderItemText",
  componentId: "sc-goz7la-0"
})(["margin:0 3px;", " ", ";"], function (props) {
  return props.isClipped && clippedStyling;
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$o, props);
});
StyledHeaderItemText.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$n = 'chrome.nav';
var colorStyles$5 = function colorStyles(props) {
  var shade = props.isDark || props.isLight ? 600 : 700;
  var backgroundColor = getColor(props.hue, shade, props.theme);
  var foregroundColor = props.isLight ? props.theme.palette.grey[800] : props.theme.palette.white;
  return css(["background-color:", ";color:", ";"], backgroundColor, foregroundColor);
};
var getNavWidth = function getNavWidth(props) {
  return "".concat(props.theme.space.base * 15, "px");
};
var getExpandedNavWidth = function getExpandedNavWidth() {
  return "200px";
};
var StyledNav = styled.nav.attrs({
  'data-garden-id': COMPONENT_ID$n,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledNav",
  componentId: "sc-6j462r-0"
})(["display:flex;position:relative;flex-direction:column;flex-shrink:0;order:-1;width:", ";font-size:", ";", ";", ";"], function (props) {
  return props.isExpanded ? getExpandedNavWidth : getNavWidth;
}, function (props) {
  return props.theme.fontSizes.md;
}, function (props) {
  return colorStyles$5(props);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$n, props);
});
StyledNav.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$m = 'chrome.header_item';
var retrieveProductColor$1 = function retrieveProductColor(props) {
  switch (props.product) {
    case 'chat':
      return PALETTE.product.chat;
    case 'connect':
      return PALETTE.product.connect;
    case 'explore':
      return PALETTE.product.explore;
    case 'guide':
      return PALETTE.product.guide;
    case 'message':
      return PALETTE.product.message;
    case 'support':
      return PALETTE.product.support;
    case 'talk':
      return PALETTE.product.talk;
    default:
      return 'inherit';
  }
};
var StyledLogoHeaderItem = styled(StyledBaseHeaderItem).attrs({
  'data-garden-id': COMPONENT_ID$m,
  'data-garden-version': '8.46.0',
  as: 'div'
}).withConfig({
  displayName: "StyledLogoHeaderItem",
  componentId: "sc-1n1d1yv-0"
})(["display:none;order:0;margin-right:", ";margin-left:", ";border-", ":", ";border-radius:0;padding:0;width:", ";height:100%;overflow:hidden;fill:", ";text-decoration:none;color:", ";", "{", "}", "{margin:0;width:", ";height:", ";}", ";"], function (props) {
  return props.theme.rtl ? "-".concat(props.theme.space.base, "px") : 'auto';
}, function (props) {
  return props.theme.rtl ? 'auto' : "-".concat(props.theme.space.base, "px");
}, function (props) {
  return props.theme.rtl ? 'left' : 'right';
}, function (props) {
  return "".concat(props.theme.borders.sm, " ").concat(getColor('neutralHue', 300, props.theme));
}, function (props) {
  return getNavWidth(props);
}, function (props) {
  return getColor('chromeHue', 700, props.theme);
}, function (props) {
  return retrieveProductColor$1(props);
}, StyledHeaderItemText, clippedStyling, StyledHeaderItemIcon, function (props) {
  return props.theme.iconSizes.lg;
}, function (props) {
  return props.theme.iconSizes.lg;
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$m, props);
});
StyledLogoHeaderItem.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$l = 'chrome.base_nav_item';
var getNavItemHeight = function getNavItemHeight(props) {
  return "".concat(props.theme.space.base * 13, "px");
};
var sizeStyles$3 = function sizeStyles(props) {
  var verticalPadding = math("(".concat(getNavItemHeight(props), " - ").concat(props.theme.iconSizes.lg, ") / 2"));
  var horizontalPadding = math("(".concat(getNavWidth(props), " - ").concat(props.theme.iconSizes.lg, ") / 4"));
  return css(["padding:", " ", ";min-height:", ";"], verticalPadding, horizontalPadding, getNavItemHeight);
};
var StyledBaseNavItem = styled.button.attrs({
  'data-garden-id': COMPONENT_ID$l,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledBaseNavItem",
  componentId: "sc-zvo43f-0"
})(["display:flex;flex-shrink:0;align-items:center;justify-content:center;transition:box-shadow 0.1s ease-in-out,background-color 0.1s ease-in-out,opacity 0.1s ease-in-out;border:none;box-sizing:border-box;background:transparent;text-decoration:none;color:inherit;font-size:inherit;", ""], function (props) {
  return sizeStyles$3(props);
});
StyledBaseNavItem.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$k = 'chrome.header';
var getHeaderHeight = function getHeaderHeight(props) {
  return getNavItemHeight(props);
};
var StyledHeader = styled.header.attrs({
  'data-garden-id': COMPONENT_ID$k,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledHeader",
  componentId: "sc-1cexpz-0"
})(["display:flex;position:", ";align-items:center;justify-content:flex-end;box-sizing:border-box;border-bottom:", ";box-shadow:", ";background-color:", ";padding:0 ", "px;height:", ";color:", ";font-size:", ";", " ", ";"], function (props) {
  return props.isStandalone && 'relative';
}, function (props) {
  return "".concat(props.theme.borders.sm, " ").concat(getColor('neutralHue', 300, props.theme));
}, function (props) {
  return props.isStandalone && props.theme.shadows.lg('0', '10px', getColor('chromeHue', 600, props.theme, 0.15));
}, function (props) {
  return props.theme.colors.background;
}, function (props) {
  return props.theme.space.base;
}, getHeaderHeight, function (props) {
  return getColor('neutralHue', 600, props.theme);
}, function (props) {
  return props.theme.fontSizes.md;
}, function (props) {
  return props.isStandalone && "\n    ".concat(StyledLogoHeaderItem, " {\n      display: inline-flex;\n    }\n  ");
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$k, props);
});
StyledHeader.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$j = 'chrome.skipnav';
var animationStyles = function animationStyles() {
  var animationName = keyframes(["0%{transform:translate(-50%,-50%);}"]);
  return css(["transition:opacity 0.2s ease-out,clip 0s linear 0.2s;opacity:0;clip:rect(0,0,0,0);&:focus{transition:opacity 0.2s ease-in-out;animation:0.2s cubic-bezier(0.15,0.85,0.35,1.2) ", ";opacity:1;clip:rect(0,150vw,100vh,-50vw);}"], animationName);
};
var colorStyles$4 = function colorStyles(theme) {
  var color = getColor('primaryHue', 600, theme);
  var borderColor = getColor('neutralHue', 300, theme);
  var boxShadow = theme.shadows.lg("".concat(theme.space.base * 5, "px"), "".concat(theme.space.base * 7, "px"), getColor('chromeHue', 600, theme, 0.15));
  return css(["border-color:", ";box-shadow:", ";background-color:", ";color:", ";&:hover,&:focus{color:", ";}"], borderColor, boxShadow, theme.colors.background, color, color);
};
var sizeStyles$2 = function sizeStyles(props) {
  var top = math("".concat(getHeaderHeight(props), " / 2"));
  var padding = "".concat(props.theme.space.base * 5, "px");
  var paddingStart = "".concat(props.theme.space.base * 4, "px");
  var fontSize = props.theme.fontSizes.md;
  var lineHeight = getLineHeight(padding, fontSize);
  return css(["top:", ";padding:", ";padding-", ":", ";line-height:", ";font-size:", ";"], top, padding, props.theme.rtl ? 'right' : 'left', paddingStart, lineHeight, fontSize);
};
var StyledSkipNav = styled.a.attrs({
  'data-garden-id': COMPONENT_ID$j,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledSkipNav",
  componentId: "sc-1tsro34-0"
})(["", ";display:inline-flex;position:absolute;left:50%;align-items:center;justify-content:center;transform:translateX(-50%);direction:", ";z-index:", ";border:", ";border-radius:", ";text-decoration:underline;white-space:nowrap;", ";&:focus{outline:none;}", ";", ";"], animationStyles(), function (props) {
  return props.theme.rtl && 'rtl';
}, function (props) {
  return props.zIndex;
}, function (props) {
  return props.theme.borders.sm;
}, function (props) {
  return props.theme.borderRadii.md;
}, function (props) {
  return sizeStyles$2(props);
}, function (props) {
  return colorStyles$4(props.theme);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$j, props);
});
StyledSkipNav.defaultProps = {
  theme: DEFAULT_THEME
};

function _extends$1() { _extends$1 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1.apply(this, arguments); }

var _ref$1 = /*#__PURE__*/React.createElement("path", {
  fill: "currentColor",
  d: "M4.441 7.38l.095.083.939.939-.708.707-.939-.939-2 2-.132.142a2.829 2.829 0 003.99 3.99l.142-.132 2-2-.939-.939.707-.708.94.94a1 1 0 01.083 1.32l-.083.094-2 2A3.828 3.828 0 01.972 9.621l.15-.158 2-2A1 1 0 014.34 7.31l.101.07zm7.413-3.234a.5.5 0 01.057.638l-.057.07-7 7a.5.5 0 01-.765-.638l.057-.07 7-7a.5.5 0 01.708 0zm3.023-3.025a3.829 3.829 0 01.15 5.257l-.15.158-2 2a1 1 0 01-1.32.083l-.094-.083-.94-.94.708-.707.939.94 2-2 .132-.142a2.829 2.829 0 00-3.99-3.99l-.142.131-2 2 .939.939-.707.708-.94-.94a1 1 0 01-.082-1.32l.083-.094 2-2a3.828 3.828 0 015.414 0z"
});

function SvgLinkStroke(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref$1);
}

var COMPONENT_ID$i = 'chrome.skipnav_icon';
var sizeStyles$1 = function sizeStyles(theme) {
  var margin = "".concat(theme.space.base * 2, "px");
  var size = theme.iconSizes.md;
  return css(["margin-", ":", ";width:", ";height:", ";"], theme.rtl ? 'left' : 'right', margin, size, size);
};
var StyledSkipNavIcon = styled(SvgLinkStroke).attrs({
  'data-garden-id': COMPONENT_ID$i,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledSkipNavIcon",
  componentId: "sc-1ika5z4-0"
})(["transform:", ";color:", ";", ";", ";"], function (props) {
  return props.theme.rtl && 'scaleX(-1)';
}, function (props) {
  return getColor('neutralHue', 600, props.theme);
}, function (props) {
  return sizeStyles$1(props.theme);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$i, props);
});
StyledSkipNavIcon.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$h = 'chrome.body';
var StyledBody = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$h,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledBody",
  componentId: "sc-c7h9kv-0"
})(["flex:1;order:1;background-color:", ";min-width:0;", ";"], function (props) {
  return getColor('neutralHue', 100, props.theme);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$h, props);
});
StyledBody.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$g = 'chrome.footer';
var getFooterHeight = function getFooterHeight(props) {
  return "".concat(props.theme.space.base * 20, "px");
};
var StyledFooter = styled.footer.attrs({
  'data-garden-id': COMPONENT_ID$g,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledFooter",
  componentId: "sc-v7lib2-0"
})(["display:flex;align-items:center;justify-content:flex-end;box-sizing:border-box;border-top:", ";background-color:", ";padding:0 ", "px;height:", ";", ";"], function (props) {
  return "".concat(props.theme.borders.sm, " ").concat(getColor('neutralHue', 300, props.theme));
}, function (props) {
  return props.theme.colors.background;
}, function (props) {
  return props.theme.space.base * 9;
}, getFooterHeight, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$g, props);
});
StyledFooter.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$f = 'chrome.content';
var StyledContent = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$f,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledContent",
  componentId: "sc-qcuzxn-0"
})(["display:flex;height:", ";line-height:", ";color:", ";font-size:", ";&:focus{outline:none;}", ";"], function (props) {
  return props.hasFooter ? "calc(100% - ".concat(math("".concat(getHeaderHeight(props), " + ").concat(getFooterHeight(props))), ")") : "calc(100% - ".concat(getHeaderHeight(props), ")");
}, function (props) {
  return getLineHeight(props.theme.lineHeights.md, props.theme.fontSizes.md);
}, function (props) {
  return props.theme.colors.foreground;
}, function (props) {
  return props.theme.fontSizes.md;
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$f, props);
});
StyledContent.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$e = 'chrome.main';
var StyledMain = styled.main.attrs({
  'data-garden-id': COMPONENT_ID$e,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledMain",
  componentId: "sc-t61cre-0"
})(["flex:1;order:1;background-color:", ";overflow:auto;:focus{outline:none;}", ";"], function (props) {
  return props.theme.colors.background;
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$e, props);
});
StyledMain.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$d = 'chrome.sidebar';
var StyledSidebar = styled.aside.attrs({
  'data-garden-id': COMPONENT_ID$d,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledSidebar",
  componentId: "sc-1q77fuw-0"
})(["flex-shrink:0;order:0;box-sizing:border-box;border-", ":", ";width:330px;overflow:auto;&:focus{outline:none;}", ";"], function (props) {
  return props.theme.rtl ? 'left' : 'right';
}, function (props) {
  return "".concat(props.theme.borders.sm, " ").concat(getColor('neutralHue', 300, props.theme));
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$d, props);
});
StyledSidebar.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$c = 'chrome.footer_item';
var StyledFooterItem = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$c,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledFooterItem",
  componentId: "sc-1cktm85-0"
})(["margin:", ";", ";"], function (props) {
  return "0 ".concat(props.theme.space.base, "px");
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$c, props);
});
StyledFooterItem.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$b = 'chrome.header_item';
var imgStyles = function imgStyles(props) {
  var size = math("".concat(getHeaderItemSize(props), " - ").concat(props.theme.space.base * 2));
  return css(["img{margin:0;border-radius:", ";width:", ";height:", ";}"], math("".concat(props.theme.borderRadii.md, " - 1")), size, size);
};
var StyledHeaderItem = styled(StyledBaseHeaderItem).attrs({
  'data-garden-id': COMPONENT_ID$b,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledHeaderItem",
  componentId: "sc-14sft6n-0"
})(["&:hover,&:focus{text-decoration:none;color:inherit;}&:focus{outline:none;}&[data-garden-focus-visible]{box-shadow:", ";}&[data-garden-focus-visible]:active{box-shadow:none;}&:hover ", ",&:hover ", ",&:active ", ",&:active ", "{color:", ";}", " ", " ", " ", ";"], function (props) {
  return props.theme.shadows.md(getColor('chromeHue', 400, props.theme, 0.35));
},
StyledHeaderItemIcon,
StyledHeaderItemText,
StyledHeaderItemIcon,
StyledHeaderItemText, function (props) {
  return getColor('chromeHue', 700, props.theme);
}, function (props) {
  return props.maxY && "\n      &[data-garden-focus-visible] {\n        box-shadow: inset ".concat(props.theme.shadows.lg(props.theme.shadowWidths.md, '0', getColor('chromeHue', 400, props.theme, 0.35)), ",\n        ").concat(props.theme.shadowWidths.md, " 0 0 0 ").concat(getColor('chromeHue', 400, props.theme, 0.35), ",\n        inset ").concat(props.theme.shadows.lg("-".concat(props.theme.shadowWidths.md), '0', getColor('chromeHue', 400, props.theme, 0.35)), ",\n        -").concat(props.theme.shadowWidths.md, " 0 0 0 ").concat(getColor('chromeHue', 400, props.theme, 0.35), ";\n      }\n  ");
}, imgStyles, function (props) {
  return props.isRound && "\n    ".concat(StyledHeaderItemIcon, " {\n      border-radius: 100px;\n    }\n  ");
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$b, props);
});
StyledHeaderItem.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$a = 'chrome.header_item_wrapper';
var StyledHeaderItemWrapper = styled(StyledBaseHeaderItem).attrs({
  'data-garden-id': COMPONENT_ID$a,
  'data-garden-version': '8.46.0',
  as: 'div'
}).withConfig({
  displayName: "StyledHeaderItemWrapper",
  componentId: "sc-1uieu55-0"
})(["", ";"], function (props) {
  return retrieveComponentStyles(COMPONENT_ID$a, props);
});
StyledHeaderItemWrapper.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$9 = 'chrome.logo_nav_item';
var retrieveProductColor = function retrieveProductColor(product) {
  switch (product) {
    case 'chat':
      return PALETTE.product.chat;
    case 'connect':
      return PALETTE.product.connect;
    case 'explore':
      return PALETTE.product.explore;
    case 'guide':
      return PALETTE.product.guide;
    case 'message':
      return PALETTE.product.message;
    case 'support':
      return PALETTE.product.support;
    case 'talk':
      return PALETTE.product.talk;
    default:
      return 'inherit';
  }
};
var colorStyles$3 = function colorStyles(props) {
  var fillColor = props.isLight ? props.theme.palette.grey[800] : props.theme.palette.white;
  var color = props.isLight || props.isDark ? fillColor : retrieveProductColor(props.product);
  return css(["color:", ";fill:", ";"], color, fillColor);
};
var StyledLogoNavItem = styled(StyledBaseNavItem).attrs({
  'data-garden-id': COMPONENT_ID$9,
  'data-garden-version': '8.46.0',
  as: 'div'
}).withConfig({
  displayName: "StyledLogoNavItem",
  componentId: "sc-saaydx-0"
})(["order:0;opacity:1;cursor:default;", ";"], function (props) {
  return colorStyles$3(props);
});
StyledLogoNavItem.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$8 = 'chrome.brandmark_nav_item';
var StyledBrandmarkNavItem = styled(StyledBaseNavItem).attrs({
  'data-garden-id': COMPONENT_ID$8,
  'data-garden-version': '8.46.0',
  as: 'div'
}).withConfig({
  displayName: "StyledBrandmarkNavItem",
  componentId: "sc-8kynd4-0"
})(["order:1;opacity:0.3;margin-top:auto;"]);
StyledBrandmarkNavItem.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$7 = 'chrome.nav_item_icon';
var StyledNavItemIcon = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$7,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledNavItemIcon",
  componentId: "sc-7w9rpt-0"
})(["align-self:flex-start;order:0;border-radius:", ";width:", ";height:", ";", ";"], function (props) {
  return props.theme.borderRadii.md;
}, function (props) {
  return props.theme.iconSizes.lg;
}, function (props) {
  return props.theme.iconSizes.lg;
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$7, props);
});
StyledNavItemIcon.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$6 = 'chrome.nav_item';
var colorStyles$2 = function colorStyles(props) {
  var BLACK = props.theme.palette.black;
  var WHITE = props.theme.palette.white;
  var currentColor;
  var hoverColor;
  if (props.isCurrent) {
    if (props.isLight) {
      currentColor = rgba(BLACK, 0.3);
    } else if (props.isDark) {
      currentColor = rgba(WHITE, 0.3);
    } else {
      currentColor = getColor(props.hue, 500, props.theme);
    }
  } else {
    hoverColor = rgba(props.isLight ? WHITE : BLACK, 0.1);
  }
  var activeColor = rgba(props.isLight ? BLACK : WHITE, 0.1);
  var focusColor = rgba(props.isLight ? BLACK : WHITE, 0.2);
  return css(["opacity:", ";background-color:", ";&:hover{opacity:1;background-color:", ";}&[data-garden-focus-visible]{opacity:1;box-shadow:inset ", ";}&:active{background-color:", ";}"], props.isCurrent ? 1 : 0.6, currentColor, hoverColor, props.theme.shadows.md(focusColor), activeColor);
};
var StyledNavItem = styled(StyledBaseNavItem).attrs({
  'data-garden-id': COMPONENT_ID$6,
  'data-garden-version': '8.46.0',
  as: 'button'
}).withConfig({
  displayName: "StyledNavItem",
  componentId: "sc-gs8mjz-0"
})(["justify-content:", ";order:1;margin:0;cursor:", ";text-align:", ";&:hover,&:focus{text-decoration:none;color:inherit;}&:focus{outline:none;}", ";", " ", ";"], function (props) {
  return props.isExpanded && 'start';
}, function (props) {
  return props.isCurrent ? 'default' : 'pointer';
}, function (props) {
  return props.isExpanded && 'inherit';
}, function (props) {
  return colorStyles$2(props);
}, function (props) {
  return props.isExpanded && "\n    ".concat(StyledNavItemIcon, " {\n      margin: 0 ").concat(math("(".concat(getNavWidth(props), " - ").concat(props.theme.iconSizes.lg, ") / 4")), ";\n    }\n  ");
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$6, props);
});
StyledNavItem.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$5 = 'chrome.nav_item_text';
var StyledNavItemText = styled.span.attrs({
  'data-garden-id': COMPONENT_ID$5,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledNavItemText",
  componentId: "sc-13m84xl-0"
})(["position:absolute;order:1;clip:rect(1px,1px,1px,1px);margin:", ";width:1px;height:1px;overflow:hidden;line-height:", ";white-space:", ";", " ", ";"], function (props) {
  return props.isExpanded && "0 ".concat(math("(".concat(getNavWidth(props), " - ").concat(props.theme.iconSizes.lg, ") / 4")));
}, function (props) {
  return getLineHeight(props.theme.space.base * 5, props.theme.fontSizes.md);
}, function (props) {
  return props.isWrapped ? 'normal' : 'nowrap';
}, function (props) {
  return props.isExpanded && "\n    ".concat(StyledNavItem, " > && {\n      position: static;\n      flex: 1;\n      clip: auto;\n      width: auto;\n      height: auto;\n      text-overflow: ellipsis;\n    }\n  ");
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$5, props);
});
StyledNavItemText.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$4 = 'chrome.subnav_item';
var colorStyles$1 = function colorStyles(props) {
  var BLACK = props.theme.palette.black;
  var WHITE = props.theme.palette.white;
  var currentColor;
  var hoverColor;
  if (props.isCurrent) {
    if (props.isLight) {
      currentColor = rgba(BLACK, 0.1);
    } else {
      currentColor = rgba(WHITE, 0.1);
    }
  } else {
    hoverColor = rgba(props.isLight ? WHITE : BLACK, 0.1);
  }
  var activeColor = rgba(props.isLight ? BLACK : WHITE, 0.03);
  var focusColor = rgba(props.isLight ? BLACK : WHITE, 0.2);
  return css(["opacity:", ";background-color:", ";&:hover{opacity:1;background-color:", ";}&[data-garden-focus-visible]{opacity:1;box-shadow:", ";}&:not([data-garden-header='true']):active{background-color:", ";}"], props.isCurrent ? '1' : '0.6', currentColor, hoverColor, props.theme.shadows.md(focusColor), activeColor);
};
var getSubNavItemHeight = function getSubNavItemHeight(props) {
  return "".concat(props.theme.space.base * 7.5, "px");
};
var StyledSubNavItem = styled.button.attrs({
  'data-garden-id': COMPONENT_ID$4,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledSubNavItem",
  componentId: "sc-1yg9dpx-0"
})(["display:flex;align-items:center;transition:box-shadow 0.1s ease-in-out,background-color 0.1s ease-in-out,opacity 0.1s ease-in-out;margin:", "px 0 0;border:none;border-radius:", ";box-sizing:border-box;background:transparent;cursor:", ";padding:", ";width:100%;min-height:", ";text-align:inherit;font-size:inherit;&,&:hover,&:focus{text-decoration:none;color:inherit;}&:focus{outline:none;}", ";", ";"], function (props) {
  return props.theme.space.base * 2;
}, function (props) {
  return props.theme.borderRadii.md;
}, function (props) {
  return props.isCurrent ? 'default' : 'pointer';
}, function (props) {
  return "0 ".concat(props.theme.space.base * 2, "px");
}, getSubNavItemHeight, function (props) {
  return colorStyles$1(props);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$4, props);
});
StyledSubNavItem.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$3 = 'chrome.subnav';
var colorStyles = function colorStyles(props) {
  var shade;
  if (props.isLight) {
    shade = 500;
  } else {
    shade = props.isDark ? 700 : 800;
  }
  var backgroundColor = getColor(props.hue, shade, props.theme);
  var foregroundColor = props.isLight ? props.theme.palette.grey[800] : props.theme.palette.white;
  return css(["background-color:", ";color:", ";"], backgroundColor, foregroundColor);
};
var StyledSubNav = styled.nav.attrs({
  'data-garden-id': COMPONENT_ID$3,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledSubNav",
  componentId: "sc-19hjou6-0"
})(["flex-direction:column;order:0;padding:", ";min-width:220px;font-size:", ";", ";& > ", ":first-child{margin-top:0;}", ";"], function (props) {
  return "".concat(props.theme.space.base * 6, "px ").concat(props.theme.space.base * 5, "px");
}, function (props) {
  return props.theme.fontSizes.md;
}, function (props) {
  return colorStyles(props);
}, StyledSubNavItem, function (props) {
  return retrieveComponentStyles('chrome.subnav', props);
});
StyledSubNav.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$2 = 'chrome.subnav_item_text';
var sizeStyles = function sizeStyles(props) {
  var baseLineHeight = props.theme.space.base * 5;
  var verticalMargin = math("(".concat(getSubNavItemHeight(props), " - ").concat(baseLineHeight, ") / 2"));
  var lineHeight = getLineHeight(baseLineHeight, props.theme.fontSizes.md);
  return css(["margin:", " 0;line-height:", ";"], verticalMargin, lineHeight);
};
var StyledSubNavItemText = styled.span.attrs({
  'data-garden-id': COMPONENT_ID$2,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledSubNavItemText",
  componentId: "sc-1hy0pn7-0"
})(["overflow:hidden;text-overflow:ellipsis;white-space:", ";", " ", ";"], function (props) {
  return props.isWrapped ? 'normal' : 'nowrap';
}, function (props) {
  return sizeStyles(props);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$2, props);
});
StyledSubNavItemText.defaultProps = {
  theme: DEFAULT_THEME
};

var COMPONENT_ID$1 = 'chrome.collapsible_sub_nav_item';
var StyledSubNavItemHeader = styled(StyledSubNavItem).attrs({
  'data-garden-id': COMPONENT_ID$1,
  'data-garden-version': '8.46.0',
  'data-garden-header': 'true'
}).withConfig({
  displayName: "StyledSubNavItemHeader",
  componentId: "sc-1vniter-0"
})(["position:relative;padding-", ":", "px;", ";"], function (props) {
  return props.theme.rtl ? 'left' : 'right';
}, function (props) {
  return props.theme.space.base * 7;
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$1, props);
});
StyledSubNavItemHeader.defaultProps = {
  theme: DEFAULT_THEME
};

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

var _ref = /*#__PURE__*/React.createElement("path", {
  fill: "currentColor",
  d: "M1.646 3.646a.5.5 0 01.638-.057l.07.057L6 7.293l3.646-3.647a.5.5 0 01.638-.057l.07.057a.5.5 0 01.057.638l-.057.07-4 4a.5.5 0 01-.638.057l-.07-.057-4-4a.5.5 0 010-.708z"
});

function SvgChevronDownStroke(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    xmlns: "http://www.w3.org/2000/svg",
    width: 12,
    height: 12,
    viewBox: "0 0 12 12",
    focusable: "false",
    role: "presentation"
  }, props), _ref);
}

var _excluded$8 = ["theme", "isExpanded"];
var COMPONENT_ID = 'chrome.collapsible_sub_nav_item_icon';
var FilteredChevronDownStrokeIcon = React__default.forwardRef(function (_ref, ref) {
  _ref.theme;
      _ref.isExpanded;
      var validProps = _objectWithoutProperties(_ref, _excluded$8);
  return React__default.createElement(SvgChevronDownStroke, _extends$2({
    ref: ref
  }, validProps));
});
FilteredChevronDownStrokeIcon.displayName = 'FilteredChevronDownStrokeIcon';
var StyledSubNavItemIcon = styled(FilteredChevronDownStrokeIcon).withConfig({
  displayName: "StyledSubNavItemIcon",
  componentId: "sc-1d02hho-0"
})(["width:", ";height:", ";"], function (props) {
  return props.theme.iconSizes.sm;
}, function (props) {
  return props.theme.iconSizes.sm;
});
StyledSubNavItemIcon.defaultProps = {
  theme: DEFAULT_THEME
};
var StyledSubNavItemIconWrapper = styled.div.attrs({
  'data-garden-id': COMPONENT_ID,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledSubNavItemIcon__StyledSubNavItemIconWrapper",
  componentId: "sc-1d02hho-1"
})(["display:flex;position:absolute;top:0;right:", ";left:", ";align-items:center;justify-content:center;width:", "px;height:", ";", "{transform:", ";transition:transform 0.25s ease-in-out;}", ";"], function (props) {
  return props.theme.rtl ? 'auto' : 0;
}, function (props) {
  return props.theme.rtl && 0;
}, function (props) {
  return props.theme.space.base * 7;
}, getSubNavItemHeight, StyledSubNavItemIcon, function (props) {
  if (props.isExpanded) {
    return css(["rotate(", "180deg)"], props.theme.rtl && '-');
  }
  return undefined;
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID, props);
});
StyledSubNavItemIconWrapper.defaultProps = {
  theme: DEFAULT_THEME
};

var PANEL_COMPONENT_ID = 'chrome.collapsible_sub_nav_item_panel';
var hiddenStyling = css(["visibility:hidden;max-height:0 !important;overflow:hidden;"]);
var StyledSubNavPanel = styled.div.attrs({
  'data-garden-id': PANEL_COMPONENT_ID,
  'data-garden-version': '8.46.0'
}).withConfig({
  displayName: "StyledSubNavPanel",
  componentId: "sc-1jv3rpv-0"
})(["transition:max-height 0.25s cubic-bezier(0.15,0.85,0.35,1.2),0.25s visibility 0s linear;height:auto;max-height:100%;", " ", "{padding-", ":", ";}", ";"], function (props) {
  return props.isHidden && hiddenStyling;
}, StyledSubNavItem, function (props) {
  return props.theme.rtl ? 'right' : 'left';
}, function (props) {
  return "".concat(props.theme.space.base * 5, "px");
}, function (props) {
  return retrieveComponentStyles(PANEL_COMPONENT_ID, props);
});
StyledSubNavPanel.defaultProps = {
  theme: DEFAULT_THEME
};

var _excluded$7 = ["hue", "isFluid"];
var Chrome = React__default.forwardRef(function (_ref, ref) {
  var hue = _ref.hue,
      isFluid = _ref.isFluid,
      props = _objectWithoutProperties(_ref, _excluded$7);
  var theme = useContext(ThemeContext);
  var isLightMemoized = useMemo(function () {
    if (hue) {
      var backgroundColor = getColor(hue, 600, theme);
      var LIGHT_COLOR = 'white';
      return readableColor(backgroundColor, LIGHT_COLOR, undefined, false
      ) === LIGHT_COLOR;
    }
    return false;
  }, [hue, theme]);
  var isLight = hue ? isLightMemoized : false;
  var isDark = hue ? !isLightMemoized : false;
  var chromeContextValue = useMemo(function () {
    return {
      hue: hue || 'chromeHue',
      isLight: isLight,
      isDark: isDark
    };
  }, [hue, isLight, isDark]);
  var environment = useDocument(theme);
  useEffect(function () {
    if (environment && !isFluid) {
      var htmlElement = environment.querySelector('html');
      if (htmlElement) {
        var defaultHtmlPosition = htmlElement.style.position;
        htmlElement.style.position = 'fixed';
        return function () {
          htmlElement.style.position = defaultHtmlPosition;
        };
      }
    }
    return undefined;
  }, [environment, isFluid]);
  return React__default.createElement(ChromeContext.Provider, {
    value: chromeContextValue
  }, React__default.createElement(StyledChrome, _extends$2({
    ref: ref
  }, props)));
});
Chrome.displayName = 'Chrome';
Chrome.propTypes = {
  hue: PropTypes.string
};

var _excluded$6 = ["targetId", "zIndex", "children"];
var SkipNav = React__default.forwardRef(function (_ref, ref) {
  var targetId = _ref.targetId,
      zIndex = _ref.zIndex,
      children = _ref.children,
      props = _objectWithoutProperties(_ref, _excluded$6);
  return React__default.createElement(StyledSkipNav, _extends$2({
    href: "#".concat(targetId),
    zIndex: zIndex,
    ref: ref
  }, props), React__default.createElement(StyledSkipNavIcon, null), children);
});
SkipNav.displayName = 'SkipNav';
SkipNav.propTypes = {
  targetId: PropTypes.string.isRequired,
  zIndex: PropTypes.number
};
SkipNav.defaultProps = {
  zIndex: 1
};

var BodyContext = React__default.createContext({
  hasFooter: true
});
var useBodyContext = function useBodyContext() {
  return useContext(BodyContext);
};

var _excluded$5 = ["hasFooter"];
var Body = React__default.forwardRef(function (_ref, ref) {
  var hasFooter = _ref.hasFooter,
      props = _objectWithoutProperties(_ref, _excluded$5);
  var bodyContextValue = useMemo(function () {
    return {
      hasFooter: !!hasFooter
    };
  }, [hasFooter]);
  return React__default.createElement(BodyContext.Provider, {
    value: bodyContextValue
  }, React__default.createElement(StyledBody, _extends$2({
    ref: ref
  }, props)));
});
Body.displayName = 'Body';
Body.propTypes = {
  hasFooter: PropTypes.bool
};

var Content = React__default.forwardRef(function (props, ref) {
  var _useBodyContext = useBodyContext(),
      hasFooter = _useBodyContext.hasFooter;
  return React__default.createElement(StyledContent, _extends$2({
    ref: ref,
    hasFooter: hasFooter
  }, props));
});
Content.displayName = 'Content';

var Main = React__default.forwardRef(function (props, ref) {
  return React__default.createElement(StyledMain, _extends$2({
    ref: ref
  }, props));
});
Main.displayName = 'Main';

var Sidebar = React__default.forwardRef(function (props, ref) {
  return React__default.createElement(StyledSidebar, _extends$2({
    ref: ref
  }, props));
});
Sidebar.displayName = 'Sidebar';

var Header = React__default.forwardRef(function (props, ref) {
  return React__default.createElement(StyledHeader, _extends$2({
    ref: ref
  }, props));
});
Header.displayName = 'Header';
Header.propTypes = {
  isStandalone: PropTypes.bool
};

var PRODUCTS = ['chat', 'connect', 'explore', 'guide', 'message', 'support', 'talk'];

var _excluded$4 = ["hasLogo", "product"];
var HeaderItem = React__default.forwardRef(function (_ref, ref) {
  var hasLogo = _ref.hasLogo,
      product = _ref.product,
      other = _objectWithoutProperties(_ref, _excluded$4);
  if (hasLogo) {
    return React__default.createElement(StyledLogoHeaderItem, _extends$2({
      ref: ref,
      product: product
    }, other));
  }
  return React__default.createElement(StyledHeaderItem, _extends$2({
    ref: ref
  }, other));
});
HeaderItem.displayName = 'HeaderItem';
HeaderItem.propTypes = {
  maxX: PropTypes.bool,
  maxY: PropTypes.bool,
  isRound: PropTypes.bool,
  product: PropTypes.oneOf(PRODUCTS),
  hasLogo: PropTypes.bool
};

var _excluded$3 = ["children"];
var HeaderItemIcon = function HeaderItemIcon(_ref) {
  var children = _ref.children,
      props = _objectWithoutProperties(_ref, _excluded$3);
  var Element = function Element(elementProps) {
    return React__default.cloneElement(Children.only(children), _objectSpread2(_objectSpread2({}, elementProps), props));
  };
  return React__default.createElement(StyledHeaderItemIcon, _extends$2({
    as: Element
  }, props));
};

var HeaderItemText = React__default.forwardRef(function (props, ref) {
  return React__default.createElement(StyledHeaderItemText, _extends$2({
    ref: ref
  }, props));
});
HeaderItemText.displayName = 'HeaderItemText';
HeaderItemText.propTypes = {
  isClipped: PropTypes.bool
};

var HeaderItemWrapper = React__default.forwardRef(function (props, ref) {
  return React__default.createElement(StyledHeaderItemWrapper, _extends$2({
    ref: ref
  }, props));
});
HeaderItemWrapper.displayName = 'HeaderItemWrapper';

var Footer = React__default.forwardRef(function (props, ref) {
  return React__default.createElement(StyledFooter, _extends$2({
    ref: ref
  }, props));
});
Footer.displayName = 'Footer';

var FooterItem = React__default.forwardRef(function (props, ref) {
  return React__default.createElement(StyledFooterItem, _extends$2({
    ref: ref
  }, props));
});
FooterItem.displayName = 'FooterItem';

var NavContext = React__default.createContext({
  isExpanded: false
});
var useNavContext = function useNavContext() {
  return useContext(NavContext);
};

var Nav = React__default.forwardRef(function (props, ref) {
  var _useChromeContext = useChromeContext(),
      hue = _useChromeContext.hue,
      isLight = _useChromeContext.isLight,
      isDark = _useChromeContext.isDark;
  var navContextValue = useMemo(function () {
    return {
      isExpanded: !!props.isExpanded
    };
  }, [props.isExpanded]);
  return React__default.createElement(NavContext.Provider, {
    value: navContextValue
  }, React__default.createElement(StyledNav, _extends$2({
    ref: ref
  }, props, {
    hue: hue,
    isLight: isLight,
    isDark: isDark
  })));
});
Nav.displayName = 'Nav';
Nav.propTypes = {
  isExpanded: PropTypes.bool
};

var _excluded$2 = ["hasLogo", "hasBrandmark", "product"];
var NavItem = React__default.forwardRef(function (_ref, ref) {
  var hasLogo = _ref.hasLogo,
      hasBrandmark = _ref.hasBrandmark,
      product = _ref.product,
      other = _objectWithoutProperties(_ref, _excluded$2);
  var _useChromeContext = useChromeContext(),
      hue = _useChromeContext.hue,
      isLight = _useChromeContext.isLight,
      isDark = _useChromeContext.isDark;
  var _useNavContext = useNavContext(),
      isExpanded = _useNavContext.isExpanded;
  if (hasLogo) {
    return React__default.createElement(StyledLogoNavItem, _extends$2({
      ref: ref,
      isDark: isDark,
      isLight: isLight,
      product: product
    }, other));
  }
  if (hasBrandmark) {
    return React__default.createElement(StyledBrandmarkNavItem, _extends$2({
      ref: ref
    }, other));
  }
  return React__default.createElement(StyledNavItem, _extends$2({
    tabIndex: 0,
    ref: ref,
    isExpanded: isExpanded,
    hue: hue,
    isDark: isDark,
    isLight: isLight
  }, other));
});
NavItem.displayName = 'NavItem';
NavItem.propTypes = {
  product: PropTypes.oneOf(PRODUCTS),
  hasLogo: PropTypes.bool,
  hasBrandmark: PropTypes.bool,
  isCurrent: PropTypes.bool
};

var _excluded$1 = ["children"];
var NavItemIcon = function NavItemIcon(_ref) {
  var children = _ref.children,
      props = _objectWithoutProperties(_ref, _excluded$1);
  var Element = function Element(elementProps) {
    return React__default.cloneElement(Children.only(children), _objectSpread2(_objectSpread2({}, elementProps), props));
  };
  return React__default.createElement(StyledNavItemIcon, _extends$2({
    as: Element
  }, props));
};

var NavItemText = React__default.forwardRef(function (props, ref) {
  var _useNavContext = useNavContext(),
      isExpanded = _useNavContext.isExpanded;
  return React__default.createElement(StyledNavItemText, _extends$2({
    ref: ref,
    isExpanded: isExpanded
  }, props));
});
NavItemText.displayName = 'NavItemText';
NavItemText.propTypes = {
  isWrapped: PropTypes.bool
};

var SubNav = React__default.forwardRef(function (props, ref) {
  var _useChromeContext = useChromeContext(),
      hue = _useChromeContext.hue,
      isLight = _useChromeContext.isLight,
      isDark = _useChromeContext.isDark;
  return React__default.createElement(StyledSubNav, _extends$2({
    ref: ref,
    hue: hue,
    isLight: isLight,
    isDark: isDark
  }, props));
});
SubNav.displayName = 'SubNav';

var SubNavItem = React__default.forwardRef(function (props, ref) {
  var _useChromeContext = useChromeContext(),
      isDark = _useChromeContext.isDark,
      isLight = _useChromeContext.isLight;
  return React__default.createElement(StyledSubNavItem, _extends$2({
    ref: ref,
    isDark: isDark,
    isLight: isLight
  }, props));
});
SubNavItem.displayName = 'SubNavItem';
SubNavItem.propTypes = {
  isCurrent: PropTypes.bool
};

var SubNavItemText = React__default.forwardRef(function (props, ref) {
  return React__default.createElement(StyledSubNavItemText, _extends$2({
    ref: ref
  }, props));
});
SubNavItemText.displayName = 'SubNavItemText';
SubNavItemText.propTypes = {
  isWrapped: PropTypes.bool
};

var _excluded = ["header", "children", "isExpanded", "onChange"];
var CollapsibleSubNavItem = React__default.forwardRef(function (_ref, ref) {
  var header = _ref.header,
      children = _ref.children,
      controlledExpanded = _ref.isExpanded,
      _onChange = _ref.onChange,
      other = _objectWithoutProperties(_ref, _excluded);
  var panelRef = useRef();
  var _useState = useState(controlledExpanded),
      _useState2 = _slicedToArray(_useState, 2),
      internalExpanded = _useState2[0],
      setInternalExpanded = _useState2[1];
  var expanded = getControlledValue(controlledExpanded, internalExpanded);
  var expandedSections = expanded ? [0] : [];
  var _useAccordion = useAccordion({
    expandedSections: expandedSections,
    onChange: function onChange() {
      var isExpanded = expandedSections.length === 0;
      if (_onChange) {
        _onChange(isExpanded);
      } else {
        setInternalExpanded(isExpanded);
      }
    }
  }),
      getHeaderProps = _useAccordion.getHeaderProps,
      getTriggerProps = _useAccordion.getTriggerProps,
      getPanelProps = _useAccordion.getPanelProps;
  useEffect(function () {
    if (expanded && panelRef.current) {
      panelRef.current.style.maxHeight = "".concat(panelRef.current.scrollHeight, "px");
    }
  }, [expanded, children]);
  return React__default.createElement("div", {
    ref: ref
  }, React__default.createElement("div", getHeaderProps({
    ariaLevel: 2
  }), React__default.createElement(StyledSubNavItemHeader, getTriggerProps(_objectSpread2({
    isExpanded: expanded,
    index: 0,
    role: null,
    tabIndex: null
  }, other)), React__default.createElement(React__default.Fragment, null, header, React__default.createElement(StyledSubNavItemIconWrapper, {
    isExpanded: expanded
  }, React__default.createElement(StyledSubNavItemIcon, null))))), React__default.createElement(StyledSubNavPanel, getPanelProps({
    index: 0,
    isHidden: !expanded,
    ref: panelRef
  }), children));
});
CollapsibleSubNavItem.propTypes = {
  header: PropTypes.any,
  isExpanded: PropTypes.bool,
  onChange: PropTypes.func,
  children: PropTypes.node
};

export { Body, Chrome, CollapsibleSubNavItem, Content, Footer, FooterItem, Header, HeaderItem, HeaderItemIcon, HeaderItemText, HeaderItemWrapper, Main, Nav, NavItem, NavItemIcon, NavItemText, Sidebar, SkipNav, SubNav, SubNavItem, SubNavItemText };
